import { FetchResult, useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
} from "@mui/material";
import { upperFirst } from "lodash";
import { DateTime } from "luxon";
import { ReactElement, useCallback, useEffect, useMemo } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import {
  AddBannerMutation,
  AddBannerMutationVariables,
  BannerLevel,
  ListAllBannersQuery,
  ListTenantsWithSubaccountsQuery,
  UpdateBannerMutation,
  UpdateBannerMutationVariables,
} from "~frontend/__generated__/graphql";
import {
  SubaccountDropdown,
  SubaccountDropdownProps,
  TenantDropdown,
  TenantDropdownProps,
} from "~frontend/components/forms/ClientDropdowns";
import {
  RhfDateTimePicker,
  RhfErrorBanner,
  RhfSelect,
  RhfTextField,
} from "~frontend/components/forms/react-hook-form-components";
import { ADD_BANNER, UPDATE_BANNER } from "~frontend/graphql/banners";
import { LIST_TENANTS_WITH_SUBACCOUNTS } from "~frontend/graphql/tenant";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

import { ALL_SENTINEL, BannerFormSchema, BannerFormValues } from "./formSchema";

const SENTINEL_LIST = [ALL_SENTINEL];

interface BannerDialogProps {
  open: boolean;
  onClose: () => void;
  banner?: ListAllBannersQuery["listAllBanners"][0] | null;
  refetch: () => void;
  mode?: "create" | "edit";
}

export default function BannerDialog({
  open,
  onClose,
  banner,
  refetch,
  mode,
}: BannerDialogProps): ReactElement {
  const getErrorMessage = useErrorMessageFormatter();
  const isEdit = mode === "edit";

  const { data: tenantsData } = useQuery<ListTenantsWithSubaccountsQuery>(
    LIST_TENANTS_WITH_SUBACCOUNTS
  );

  const [addBanner] = useMutation<
    AddBannerMutation,
    AddBannerMutationVariables
  >(ADD_BANNER, {
    onErrorSnackbarMessage: "Error creating banner",
  });

  const [updateBanner] = useMutation<
    UpdateBannerMutation,
    UpdateBannerMutationVariables
  >(UPDATE_BANNER, {
    onErrorSnackbarMessage: "Error updating banner",
  });

  const tenants = useMemo(() => tenantsData?.listTenants ?? [], [tenantsData]);

  const getDefaultValues = useCallback((): BannerFormValues => {
    const now = DateTime.now().toUTC().toISO()!;
    const startsAt = banner?.startsAt
      ? (DateTime.fromMillis(banner.startsAt).toUTC().toISO() ?? now)
      : now;

    return {
      tenantIds: banner?.tenant?.id ? [banner.tenant.id] : SENTINEL_LIST,
      subaccountIds: banner?.subaccount?.id
        ? [banner.subaccount.id]
        : SENTINEL_LIST,
      level: banner?.level ?? BannerLevel.Info,
      message: banner?.message ?? "",
      link: banner?.link ?? "",
      linkText: banner?.linkText ?? "",
      startsAt,
      expiresAt: banner?.expiresAt
        ? (DateTime.fromMillis(banner.expiresAt).toUTC().toISO() ?? undefined)
        : undefined,
    };
  }, [banner]);

  const methods = useForm<BannerFormValues>({
    resolver: zodResolver(BannerFormSchema),
    defaultValues: getDefaultValues(),
    mode: "onBlur",
  });

  const { control, handleSubmit, formState, setError, reset, setValue } =
    methods;

  // Reset form when dialog opens or banner changes
  useEffect(() => {
    if (open) {
      reset(getDefaultValues());
    }
  }, [open, reset, getDefaultValues]);

  const selectedTenantIds = useWatch({ control, name: "tenantIds" }) ?? [];
  const selectedSubaccountIds =
    useWatch({ control, name: "subaccountIds" }) ?? [];

  const isAllTenants = selectedTenantIds.includes(ALL_SENTINEL);
  const isSingleTenant = !isAllTenants && selectedTenantIds.length === 1;

  // Calculate total number of banners to be created
  const totalBanners =
    (selectedTenantIds.filter(id => id !== ALL_SENTINEL).length || 1) *
    (selectedSubaccountIds.filter(id => id !== ALL_SENTINEL).length || 1);

  async function onSubmit(values: BannerFormValues) {
    const bannerBasics = {
      level: values.level,
      message: values.message,
      link: values.link || undefined,
      linkText: values.linkText || undefined,
      startsAt: values.startsAt ?? undefined,
      expiresAt: values.expiresAt ?? undefined,
    };

    try {
      if (isEdit && banner) {
        // Update existing banner
        await updateBanner({
          variables: {
            input: {
              id: banner.id,
              ...bannerBasics,
            },
          },
        });
      } else {
        // Create new banner(s)
        // Convert ALL_SENTINEL to undefined for submission
        const tenantIds = values.tenantIds.includes(ALL_SENTINEL)
          ? [undefined]
          : values.tenantIds;
        const subaccountIds = values.subaccountIds.includes(ALL_SENTINEL)
          ? [undefined]
          : values.subaccountIds;

        // Create a banner for each combination of tenant and subaccount
        const bannerPromises: Promise<FetchResult<AddBannerMutation>>[] = [];
        tenantIds.forEach(tenantId => {
          subaccountIds.forEach(subaccountId => {
            bannerPromises.push(
              addBanner({
                variables: {
                  input: {
                    tenantId: tenantId,
                    subaccountId: subaccountId,
                    ...bannerBasics,
                  },
                },
              })
            );
          });
        });

        await Promise.all(bannerPromises);
      }

      refetch();
      onClose();
      reset();
    } catch (e) {
      setError("root", { message: getErrorMessage(e) });
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            {isEdit
              ? "Edit Banner"
              : banner
                ? "Duplicate Banner"
                : "New Banner"}
          </DialogTitle>
          <DialogContent>
            <RhfErrorBanner control={control} />

            {/* Tenant Selection */}
            <TenantDropdown
              {...({
                tenants,
                value: isEdit
                  ? selectedTenantIds[0]
                  : selectedTenantIds.includes(ALL_SENTINEL)
                    ? []
                    : selectedTenantIds,
                onChange: (newIds: string[]) => {
                  const finalIds = newIds.length === 0 ? SENTINEL_LIST : newIds;
                  setValue("tenantIds", finalIds, { shouldValidate: true });

                  // Clear subaccounts when tenant selection changes
                  if (selectedTenantIds !== finalIds) {
                    setValue("subaccountIds", SENTINEL_LIST, {
                      shouldValidate: true,
                    });
                  }
                },
                disabled: isEdit,
                label: "Tenant",
                placeholder: selectedTenantIds.includes(ALL_SENTINEL)
                  ? "Apply to all tenants"
                  : "Search tenants...",
                ...(!isEdit && { multiple: true as const }),
              } as TenantDropdownProps)}
            />

            {/* Subaccount Selection (only for single tenant) */}
            {isSingleTenant && (
              <SubaccountDropdown
                {...({
                  tenants,
                  selectedTenantId: selectedTenantIds[0],
                  value: isEdit
                    ? selectedSubaccountIds[0]
                    : selectedSubaccountIds.includes(ALL_SENTINEL)
                      ? []
                      : selectedSubaccountIds,
                  onChange: (newIds: string[]) => {
                    const finalIds =
                      newIds.length === 0 ? SENTINEL_LIST : newIds;
                    setValue("subaccountIds", finalIds, {
                      shouldValidate: true,
                    });
                  },
                  disabled: isEdit,
                  label: "Subaccounts (optional)",
                  placeholder: selectedSubaccountIds.includes(ALL_SENTINEL)
                    ? "All"
                    : "Search subaccounts...",
                  ...(!isEdit && { multiple: true as const }),
                } as SubaccountDropdownProps)}
              />
            )}

            <RhfSelect
              control={control}
              name="level"
              label="Banner Type"
              fullWidth
            >
              {(
                [
                  BannerLevel.Info,
                  BannerLevel.Warning,
                  BannerLevel.Error,
                ] as const
              ).map(level => (
                <MenuItem key={level} value={level}>
                  {upperFirst(level.toLowerCase())}
                </MenuItem>
              ))}
            </RhfSelect>

            <RhfTextField
              control={control}
              name="message"
              label="Message"
              multiline
              rows={4}
              fullWidth
              characterLimit={500}
            />

            <RhfTextField
              control={control}
              name="link"
              label="Link"
              optional
              placeholder="http://scaletowin.com"
              fullWidth
            />

            <RhfTextField
              control={control}
              name="linkText"
              label="Link Text"
              optional
              placeholder="Click Here"
              characterLimit={30}
              fullWidth
            />

            <RhfDateTimePicker
              control={control}
              name="startsAt"
              dateLabel="Start Date"
              timeLabel="Start Time"
            />

            <RhfDateTimePicker
              control={control}
              name="expiresAt"
              dateLabel="End Date"
              timeLabel="End Time"
              optional
            />
          </DialogContent>

          <DialogActions sx={{ px: 3.3, pb: 2 }}>
            <Button
              disabled={formState.isSubmitting}
              onClick={onClose}
              color="secondary"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={formState.isSubmitting}
              color="primary"
              variant="contained"
            >
              {formState.isSubmitting
                ? isEdit
                  ? "Updating..."
                  : "Creating..."
                : isEdit
                  ? "Update Banner"
                  : totalBanners > 1
                    ? `Create ${totalBanners} Banners`
                    : "Create Banner"}
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
}
